import {generateUniqueId, COLOR_GREEN, levelData } from '../constants';
import { mapDndBItemsToLairActions } from '../mappers/DndBItemsToLairActionsMapper';

const dndBNumberToString = {
    1: "Blinded Condition",  
    2: "Charmed Condition",
    3: "Deafened Condition",
    4: "Exhaustion Condition",
    5: "Frightened Condition",
    6: "Grappled Condition",
    7: "Incapacitated Condition",
    8: "Invisible Condition",
    9: "Paralyzed Condition",
    10: "Petrified Condition",
    11: "Poisoned Condition",
    12: "Prone Condition",
    13: "Restrained Condition",
    14: "Stunned Condition",
    15: "Unconscious Condition"
}

const getDnDBEffectsFromConditions = (conditions) => {
    let conditionObjs = []
    conditions.forEach(c => {
        if(dndBNumberToString[c.id] !== "Exhaustion") { //ignore exhaustion since its speciala
            conditionObjs.push({effectName: dndBNumberToString[c.id], addedBy: 'DndB', turnAdded: '1.1', duration: '∞'})
        }
    });

    return conditionObjs
}

function calcSave(skill, profBonus, proficiency, saveModifier) {
    if(!skill && skill !== 0) return 0

    let baseSave = Math.floor((skill-10)/2);
    let save = proficiency 
        ? baseSave + profBonus 
        : baseSave;
    return save + saveModifier;
}

function getClasses(classes) {
    if(!classes || classes.length === 0) return ""
    let classNames = classes.map(c => c.definition.name)
    return classNames.join(", ")
}

function getLevel(classes) {
    if(!classes || classes.length === 0) return 1
    let level = 0;
    classes.map(c => level += c.level)
    return level
}

const getSpellName = (spell) => spell?.definition?.name || spell?.name;

const getActionEntries = (description, snippet) => {
    const html = description || snippet;
    if(typeof html !== 'string' || !html.trim()) return [];

    const container = document.createElement('div');
    container.innerHTML = html;

    const paragraphs = Array.from(container.querySelectorAll('p'))
        .map(paragraph => paragraph.textContent.trim())
        .filter(Boolean);
    if(paragraphs.length > 0) return paragraphs;

    const text = container.textContent.replace(/\s+/g, ' ').trim();
    return text ? [text] : [];
};

const mapDndBActions = (actions) => Object.values(actions || {})
    .flatMap(categoryActions => Array.isArray(categoryActions) ? categoryActions : [])
    .filter(action => action?.name && !action.name.includes("Circle Spell"))
    .map(action => { 
            return {
                name: action.name,
                entries: getActionEntries(action.description, action.snippet)
            } 
    });

const addUniqueSpell = (spellList, spellName) => {
    if(spellName && !spellList.includes(spellName)) {
        spellList.push(spellName);
    }
};

const mapDndBSpellsToSpellcasting = (dndBeyondRes, spellCastingInfo=null) => {
    const spellSources = Object.values(dndBeyondRes?.spells || {})
        .flatMap(sourceSpells => Array.isArray(sourceSpells) ? sourceSpells : []);
    const classSpells = (dndBeyondRes?.classSpells || [])
        .flatMap(classSpellList => Array.isArray(classSpellList?.spells) ? classSpellList.spells : []);
    const allSpells = [...spellSources, ...classSpells];
    const spellcasting = {
        name: "Spellcasting",
        type: "spellcasting",
        headerEntries: spellCastingInfo ? `${spellCastingInfo.ability} is your spellcasting ability for your ${spellCastingInfo.class} spells. ${spellCastingInfo.from ? " - " + spellCastingInfo.from : ""}` : "",
        spellSaveMod: spellCastingInfo ? spellCastingInfo.spellSaveMod : 0,
        displayAs: "action",
        spells: {}
    };
    const restKeyByResetType = {
        dawn: "daily",
        day: "daily",
        daily: "daily",
        longrest: "lr",
        "long rest": "lr",
        shortrest: "sr",
        "short rest": "sr",
        week: "week",
        weekly: "week",
        1: "lr",
        2: "sr"
    };

    allSpells.forEach(spell => {
        const spellName = getSpellName(spell);
        if(!spellName) return;

        const spellReference = `{@spell ${spellName}}`;
        const limitedUse = spell.limitedUse;
        if(limitedUse) {
            const restKey = restKeyByResetType[String(limitedUse.resetType || "").toLowerCase()];
            if(restKey) {
                const uses = Number(limitedUse.maxUses) || 1;
                spellcasting[restKey] ??= {};
                spellcasting[restKey][`${uses}e`] ??= [];
                addUniqueSpell(spellcasting[restKey][`${uses}e`], spellReference);
                return;
            }
        }

        const level = String(spell?.definition?.level ?? spell?.level ?? 0);
        if(level === "0") {
            spellcasting.will ??= [];
            addUniqueSpell(spellcasting.will, spellReference);
            return;
        }

        spellcasting.spells[level] ??= {spells: []};
        addUniqueSpell(spellcasting.spells[level].spells, spellReference);
    });

    (dndBeyondRes?.spellSlots || []).forEach(slot => {
        const level = String(slot?.level);
        if(spellcasting.spells[level]) {
            const available = Number(slot.available);
            if(available > 0) spellcasting.spells[level].slots = available;
        }
    });

    if(Object.keys(spellcasting.spells).length === 0) delete spellcasting.spells;
    const hasSpells = Object.keys(spellcasting).some(key => ![
        "name",
        "type",
        "headerEntries",
        "displayAs"
    ].includes(key));
    return hasSpells ? [spellcasting] : [];
};

export const DndBCharacterToDmBMapper = (dndBeyondRes, encounterGuid, skillDetails=undefined) => {
    // Full info is available from dndB
    
    if(skillDetails) {
        let {maxHp, armorClass, speed, inventory, skills_json_array, proficiencies, initExtraBonus, advantages, spellCastingInfo, saveModifiers} = skillDetails;
        let calculatedHp = dndBeyondRes.overrideHitPoints ? dndBeyondRes.overrideHitPoints : maxHp;
        let level = getLevel(dndBeyondRes.classes)
        let prof_bonus = levelData[level]?.profBonus || 0;
        let spells = mapDndBSpellsToSpellcasting(dndBeyondRes, spellCastingInfo)

        return {
            "name": dndBeyondRes.name,
            "name_default": dndBeyondRes.name,
            "from": "dnd_b",
            "creatureGuid": generateUniqueId(),
            "encounterGuid": encounterGuid,
            "status": dndBeyondRes.status === 0 ? 200 : dndBeyondRes.status,
            "dnd_b_player_id": dndBeyondRes.id.toString(),
            "link": dndBeyondRes.link,
            "avatarUrl": dndBeyondRes.decorations.avatarUrl || 'https://www.dndbeyond.com/Content/Skins/Waterdeep/images/icons/monsters/humanoid.jpg',
            "hit_points": calculatedHp,
            "hit_points_default": calculatedHp,
            "hit_points_current": calculatedHp - dndBeyondRes.removedHitPoints || 0,
            "hit_points_temp": dndBeyondRes.temporaryHitPoints || 0,
            "hit_points_override": dndBeyondRes.overrideHitPoints || 0, //dnd beyonds override max hp
            "hit_points_modifier": dndBeyondRes.bonusHitPoints || 0, //dnd beyonds max hp modifier
            "initiative": 0,
            "classes": getClasses(dndBeyondRes.classes),
            "level": level,
            "initExtraBonus": initExtraBonus || 0, // 'Alert'
            "spellcasting": spells,
            "actions": mapDndBActions(dndBeyondRes.actions),
            "prof_bonus": prof_bonus,
            "proficiencies": proficiencies,
            "advantages": advantages,
            "last_damage": null,
            "death_saves_failure_count": dndBeyondRes?.deathSaves?.failCount || 0,
            "death_saves_success_count": dndBeyondRes?.deathSaves?.successCount || 0,
            "exhaustionLvl": dndBeyondRes.conditions.find(obj => obj.id === 4)?.level || 0,
            "path": "",
            "type": "player",
            "alignment": "ally",
            "groupGuid": null,
            "border": COLOR_GREEN,
            "effectObjs": getDnDBEffectsFromConditions(dndBeyondRes.conditions),
            "creature_type": dndBeyondRes.race.fullName,
            "inspiration": dndBeyondRes.inspiration,
            "bardic_inspiration": false,
            "armor_class": armorClass || 0,
            "skills": "",
            "hidden": false,
            "environments": '',
            "legendary_actions": [],
            "lair_actions": [],
            "regional_effects": [],
            "campaign": dndBeyondRes?.campaign?.name,
            "strength": skills_json_array[0]["total score"],
            "dexterity": skills_json_array[1]["total score"],
            "constitution": skills_json_array[2]["total score"],
            "intelligence": skills_json_array[3]["total score"],
            "wisdom": skills_json_array[4]["total score"],
            "charisma": skills_json_array[5]["total score"],
            "strength_save": calcSave(skills_json_array[0]["total score"], prof_bonus, proficiencies["strength-saving-throws"], saveModifiers[0]),
            "dexterity_save": calcSave(skills_json_array[1]["total score"], prof_bonus, proficiencies["dexterity-saving-throws"], saveModifiers[1]),
            "constitution_save": calcSave(skills_json_array[2]["total score"], prof_bonus, proficiencies["constitution-saving-throws"], saveModifiers[2]),
            "intelligence_save": calcSave(skills_json_array[3]["total score"], prof_bonus, proficiencies["intelligence-saving-throws"], saveModifiers[3]),
            "wisdom_save": calcSave(skills_json_array[4]["total score"], prof_bonus, proficiencies["wisdom-saving-throws"], saveModifiers[4]),
            "charisma_save": calcSave(skills_json_array[5]["total score"], prof_bonus, proficiencies["charisma-saving-throws"], saveModifiers[5]),
            "speed": {'walk': speed},
            "inventory": mapDndBItemsToLairActions(inventory, skills_json_array, proficiencies, prof_bonus),
            "damage_vulnerabilities": "",
            "damage_resistances":  "",
            "damage_immunities": "",
            "condition_immunities": "",
            "senses": "",
        }

    } else {
        return {
            "name": dndBeyondRes.name,
            "name_default": dndBeyondRes.name || '',
            "from": "dnd_b",
            "creatureGuid": generateUniqueId(),
            "encounterGuid": encounterGuid,
            "status": 403,
            "dnd_b_player_id": dndBeyondRes.id,
            "link": "",
            "avatarUrl": 'https://www.dndbeyond.com/Content/Skins/Waterdeep/images/icons/monsters/humanoid.jpg',
            "hit_points": 10,
            "hit_points_default": 1,
            "hit_points_current": 10,
            "hit_points_temp": 0,
            "hit_points_override": 0, 
            "hit_points_modifier": 0, 
            "initiative": 0,
            "last_damage": null,
            "death_saves_failure_count": dndBeyondRes?.deathSaves?.failCount || 0,
            "death_saves_success_count": dndBeyondRes?.deathSaves?.successCount || 0,
            "exhaustionLvl": 0,
            "path": "",
            "type": "player",
            "alignment": "ally",
            "groupGuid": null,
            "border": COLOR_GREEN,
            "effectObjs": [],
            "creature_type": dndBeyondRes.race || '',
            "inspiration": dndBeyondRes.inspiration,
            "bardic_inspiration": false,
            "armor_class": 10,
            "inventory": null,
            "skills":  "",
            "spellcasting": mapDndBSpellsToSpellcasting(dndBeyondRes),   
            "actions": mapDndBActions(dndBeyondRes.actions),
            "hidden": false,
            "environments": '',
            "legendary_actions": [],
            "lair_actions": [],
            "regional_effects": [],
            "campaign": null,
            "speed": {'walk': 30},
            "strength_save": 10,
            "dexterity_save": 10,
            "constitution_save": 10,
            "intelligence_save": 10,
            "charisma_save": 10,
            "wisdom_save": 10,
            "strength": 10,
            "dexterity": 10,
            "constitution": 10,
            "intelligence":10,
            "wisdom": 10,
            "charisma": 10,
            "proficiencies": {},
            "advantages": {},
            "classes": getClasses(dndBeyondRes.classes),
            "level": 0,
            "prof_bonus": 0,
            "damage_vulnerabilities": "",
            "damage_resistances":  "",
            "damage_immunities": "",
            "condition_immunities": "",
            "senses": "",
        }
    }
};

export default DndBCharacterToDmBMapper;